import { checkEnableParseJSON, formatDate, setArrayLength } from 'components/helper/helper';
import React from 'react';
import { Typography } from '@mui/material';

interface Props {
  dataValues: any;
  label?: string;
}

// Helper function to format boolean values
const formatBoolean = (value: any): string => {
  if (value === true || value === 'true') return 'Yes';
  if (value === false || value === 'false') return 'No';
  return value || '-';
};

// Legal Guardian info is stored as a double-encoded JSON string on the household
// record (legal_guardian -> stringified object whose own csu_info field is also
// stringified), so both layers need to be parsed before rendering.
const parseLegalGuardianData = (rawDataValues: any) => {
  // Fallback to the raw value itself when it isn't a JSON string (e.g. an older
  // record already stored as a plain object) so we never clobber good data with null.
  const parsedLegalGuardian = checkEnableParseJSON(rawDataValues, rawDataValues);
  return {
    ...parsedLegalGuardian,
    csu_info: checkEnableParseJSON(parsedLegalGuardian?.csu_info, parsedLegalGuardian?.csu_info),
  };
};

const LegalGuardianSum = (props: Props) => {
  const dataValues = parseLegalGuardianData(props.dataValues);

  const [institutions, setInstitutions] = React.useState<any>();

  React.useEffect(() => {
    const collegeArray =
      checkEnableParseJSON(dataValues?.parent_graduation_college_array ?? []) ?? [];
    const result = collegeArray.map((e: any, index: number) => {
      if (!e) return '';
      const degreeList = setArrayLength(
        [
          e?.degreesReceived1 + ' ' + e?.graduationYear1,
          e?.degreesReceived2 + ' ' + e?.graduationYear2,
          e?.degreesReceived3 + ' ' + e?.graduationYear3,
          e?.degreesReceived4 + ' ' + e?.graduationYear4,
          e?.degreesReceived5 + ' ' + e?.graduationYear5,
        ],
        e?.degreesNumber
      );
      return {
        isNew: e?.school?.id ? false : true,
        name: `${e?.school?.name} - ${degreeList.join(',')} `,
      };
    });
    if (result.length) {
      setInstitutions(result);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dataValues]);

  const isCaliforniaResident = dataValues?.csu_info?.isCurrentPermanentResidentOfCalifornia === 'true';
  const isNotCaliforniaResident = dataValues?.csu_info?.isCurrentPermanentResidentOfCalifornia === 'false';
  const notLivedSinceBirth = dataValues?.csu_info?.isLivedInCaliforniaContinuouslySinceBirth === 'false';

  const isEmployedOrRetired = ['Employed', 'Retired'].includes(dataValues?.employment_status);
  const isCurrentlyEmployedOrRetiredFromCollege =
    dataValues?.currently_employed_or_retire_from_college === 'Currently employed at a college/university' ||
    dataValues?.currently_employed_or_retire_from_college === 'Retired from a college/university';

  return (
    <div className="gm-summary-container">
      <div className="d-flex justify-content-between mb-4">
        <Typography sx={{ fontSize: '1.75rem', fontWeight: '500', color: '#4E5A6F' }}>
          {props.label || 'Legal Guardian'}
        </Typography>
      </div>

      {/* Basic & Contact Information Section */}
      <div className="gm-summary-section">
        <div className="gm-summary-section-title">
          Basic & Contact Information
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Prefix</div>
            <div className="gm-summary-field-value">
              {dataValues?.prefix || '-'}
            </div>
          </div>
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">First/Given name*</div>
            <div className="gm-summary-field-value">
              {dataValues?.first_name || '-'}
            </div>
          </div>
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Middle Initial</div>
            <div className="gm-summary-field-value">
              {dataValues?.middle_name || '-'}
            </div>
          </div>
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Last Name*</div>
            <div className="gm-summary-field-value">
              {dataValues?.last_name || '-'}
            </div>
          </div>
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Former Last Name</div>
            <div className="gm-summary-field-value">
              {dataValues?.former_last_name || '-'}
            </div>
          </div>
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Suffix</div>
            <div className="gm-summary-field-value">
              {dataValues?.suffix || '-'}
            </div>
          </div>
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Relationship to you*</div>
            <div className="gm-summary-field-value">
              {dataValues?.step_parent_relationship || '-'}
            </div>
          </div>
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Email Address*</div>
            <div className="gm-summary-field-value">
              {dataValues?.step_parent_email || '-'}
            </div>
          </div>
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Country Code</div>
            <div className="gm-summary-field-value">
              {dataValues?.country_code || '-'}
            </div>
          </div>
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Phone Number</div>
            <div className="gm-summary-field-value">
              {dataValues?.phone_number || '-'}
            </div>
          </div>
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Preferred phone*</div>
            <div className="gm-summary-field-value">
              {dataValues?.phone_number_type || '-'}
            </div>
          </div>
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Parent Address is*</div>
            <div className="gm-summary-field-value">
              {dataValues?.csu_info?.parent_address_is || '-'}
            </div>
          </div>
        </div>

        {dataValues?.csu_info?.parent_address_is === 'A different address' && (
          <div className="gm-summary-conditional-section">
            <div className="gm-summary-conditional-title">
              Different Address Information
            </div>
            <div className="gm-summary-field-grid">
              <div className="gm-summary-field-item">
                <div className="gm-summary-field-label">Country*</div>
                <div className="gm-summary-field-value">
                  {dataValues?.csu_info?.different_parent_address_country || '-'}
                </div>
              </div>
              <div className="gm-summary-field-item">
                <div className="gm-summary-field-label">Address Line 1*</div>
                <div className="gm-summary-field-value">
                  {dataValues?.csu_info?.different_parent_address_line_1 || '-'}
                </div>
              </div>
            </div>
            <div className="gm-summary-field-grid">
              <div className="gm-summary-field-item">
                <div className="gm-summary-field-label">Address Line 2</div>
                <div className="gm-summary-field-value">
                  {dataValues?.csu_info?.different_parent_address_line_2 || '-'}
                </div>
              </div>
              <div className="gm-summary-field-item">
                <div className="gm-summary-field-label">City*</div>
                <div className="gm-summary-field-value">
                  {dataValues?.csu_info?.different_parent_address_city || '-'}
                </div>
              </div>
            </div>
            <div className="gm-summary-field-grid">
              {dataValues?.csu_info?.different_parent_address_country === 'United States of America' ? (
                <div className="gm-summary-field-item">
                  <div className="gm-summary-field-label">State*</div>
                  <div className="gm-summary-field-value">
                    {dataValues?.csu_info?.different_parent_address_state || '-'}
                  </div>
                </div>
              ) : (
                <div className="gm-summary-field-item">
                  <div className="gm-summary-field-label">Province*</div>
                  <div className="gm-summary-field-value">
                    {dataValues?.csu_info?.different_parent_address_province || '-'}
                  </div>
                </div>
              )}
              <div className="gm-summary-field-item">
                <div className="gm-summary-field-label">Zip Code*</div>
                <div className="gm-summary-field-value">
                  {dataValues?.csu_info?.different_parent_address_zip_code || '-'}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Authorization Section */}
      <div className="gm-summary-section">
        <div className="gm-summary-section-title">
          Authorization
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Do you give permission for the admission office to communicate with your Parent/Guardian regarding your application?*</div>
            <div className="gm-summary-field-value">
              {formatBoolean(dataValues?.csu_info?.is_give_permission_communicate_to)}
            </div>
          </div>
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Do you give permission for your parent/guardian to receive university communications? These may include new student and housing information, parent activities, volunteer and charitable opportunities, and general campus information.*</div>
            <div className="gm-summary-field-value">
              {formatBoolean(dataValues?.csu_info?.is_give_permission_to_receive_information)}
            </div>
          </div>
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">I authorize the use of this parent/guardian information to be used as my emergency contact when I enroll at a CSU.</div>
            <div className="gm-summary-field-value">
              {formatBoolean(dataValues?.csu_info?.authorize_parent_guardian_as_emergency_contact)}
            </div>
          </div>
        </div>
        <div className="gm-summary-checkbox-container">
          <div className="gm-summary-text-primary">
            <span
              style={{
                marginRight: '12px',
                fontSize: '18px',
                color: formatBoolean(dataValues?.csu_info?.is_authorized_UC_to_share_contact_information) ? '#1890ff' : '#d9d9d9',
                fontWeight: formatBoolean(dataValues?.csu_info?.is_authorized_UC_to_share_contact_information) ? 'bold' : 'normal',
              }}
            >
              {formatBoolean(dataValues?.csu_info?.is_authorized_UC_to_share_contact_information) ? '✓' : '○'}
            </span>
            I authorize the University of California to share my parent's contact information.
          </div>
        </div>
      </div>

      {/* Employment Section */}
      <div className="gm-summary-section">
        <div className="gm-summary-section-title">
          Employment
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Occupation (former occupation, if retired or deceased)</div>
            <div className="gm-summary-field-value">
              {dataValues?.occupation || '-'}
            </div>
          </div>
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Employment status*</div>
            <div className="gm-summary-field-value">
              {dataValues?.employment_status || '-'}
            </div>
          </div>
        </div>

        {dataValues?.occupation === 'Other' && (
          <div className="gm-summary-field-grid">
            <div className="gm-summary-field-item">
              <div className="gm-summary-field-label">Other Occupation Details</div>
              <div className="gm-summary-field-value">
                {dataValues?.other_occupation_detail || '-'}
              </div>
            </div>
          </div>
        )}

        {isEmployedOrRetired && (
          <div className="gm-summary-field-grid">
            <div className="gm-summary-field-item">
              <div className="gm-summary-field-label">Is parent currently employed by or retired from a college or university?</div>
              <div className="gm-summary-field-value">
                {dataValues?.currently_employed_or_retire_from_college || '-'}
              </div>
            </div>
            {dataValues?.currently_employed_or_retire_from_college !== 'Currently employed at a college/university' && (
              <div className="gm-summary-field-item">
                <div className="gm-summary-field-label">Name of Current Employer</div>
                <div className="gm-summary-field-value">
                  {dataValues?.current_employer || '-'}
                </div>
              </div>
            )}
          </div>
        )}

        {isEmployedOrRetired && isCurrentlyEmployedOrRetiredFromCollege && (
          <div className="gm-summary-field-grid">
            <div className="gm-summary-field-item">
              <div className="gm-summary-field-label">College/University employer lookup</div>
              <div className="gm-summary-field-value" style={{
                color: checkEnableParseJSON(dataValues?.college_or_university)?.id ? '#4d88f0' : 'black'
              }}>
                {checkEnableParseJSON(dataValues?.college_or_university)
                  ? checkEnableParseJSON(dataValues?.college_or_university)?.name
                  : dataValues?.college_or_university || '-'}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Education Section */}
      <div className="gm-summary-section">
        <div className="gm-summary-section-title">
          Education
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Education Level</div>
            <div className="gm-summary-field-value">
              {dataValues?.education_level || '-'}
            </div>
          </div>
          {institutions && (
            <div className="gm-summary-field-item">
              <div className="gm-summary-field-label">Total number of institutions attended</div>
              <div className="gm-summary-field-value">
                {institutions?.length || '-'}
              </div>
            </div>
          )}
        </div>
        {institutions && institutions.length > 0 && (
          <div className="gm-summary-conditional-section">
            <div className="gm-summary-conditional-title">
              Institutions attended and degrees received
            </div>
            {institutions.map((e: any, index: number) => (
              <div key={index} className="gm-summary-field-grid">
                <div className="gm-summary-field-item">
                  <div className="gm-summary-field-label"></div>
                  <div className="gm-summary-field-value" style={{
                    color: e.isNew ? 'black' : '#4d88f0'
                  }}>
                    {e.name || '-'}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Residency Section */}
      <div className="gm-summary-section">
        <div className="gm-summary-section-title">
          Residency
        </div>
        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Is your parent/guardian a current permanent resident of California?*</div>
            <div className="gm-summary-field-value">
              {formatBoolean(dataValues?.csu_info?.isCurrentPermanentResidentOfCalifornia)}
            </div>
          </div>
        </div>

        {isNotCaliforniaResident && (
          <div className="gm-summary-field-grid">
            <div className="gm-summary-field-item">
              <div className="gm-summary-field-label">Which U.S. State/possession does your parent/guardian regard as her permanent home?</div>
              <div className="gm-summary-field-value">
                {dataValues?.csu_info?.stateRegardAsPermanentHome || '-'}
              </div>
            </div>
          </div>
        )}

        <div className="gm-summary-field-grid">
          <div className="gm-summary-field-item">
            <div className="gm-summary-field-label">Has your parent/guardian lived in California continuously since birth?*</div>
            <div className="gm-summary-field-value">
              {formatBoolean(dataValues?.csu_info?.isLivedInCaliforniaContinuouslySinceBirth)}
            </div>
          </div>
        </div>

        {notLivedSinceBirth && (
          <>
            <div className="gm-summary-field-grid">
              <div className="gm-summary-field-item">
                <div className="gm-summary-field-label">When did his/her current stay in California begin?*</div>
                <div className="gm-summary-field-value">
                  {formatDate(dataValues?.csu_info?.whenStayInCaliforniaBegin) || '-'}
                </div>
              </div>
            </div>
            <div className="gm-summary-field-grid">
              <div className="gm-summary-field-item">
                <div className="gm-summary-field-label">Prior Country*</div>
                <div className="gm-summary-field-value">
                  {dataValues?.csu_info?.priorCountry || '-'}
                </div>
              </div>
              <div className="gm-summary-field-item">
                <div className="gm-summary-field-label">Prior State/Territory*</div>
                <div className="gm-summary-field-value">
                  {dataValues?.csu_info?.priorState || '-'}
                </div>
              </div>
            </div>
            <div className="gm-summary-field-grid">
              <div className="gm-summary-field-item">
                <div className="gm-summary-field-label">From when to when did he/she live there*</div>
                <div className="gm-summary-field-value">
                  {formatDate(dataValues?.csu_info?.fromWhenToWhenLiveThere) || '-'}
                </div>
              </div>
              <div className="gm-summary-field-item">
                <div className="gm-summary-field-label">To*</div>
                <div className="gm-summary-field-value">
                  {formatDate(dataValues?.csu_info?.ToWhenToWhenLiveThere) || '-'}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LegalGuardianSum;
